import {
    createContext,
    PropsWithChildren,
    useContext,
    useEffect,
    useState
} from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import axios, { AxiosResponse } from 'libs/axios';
import { useNotification } from 'libs/notification';
import { Button, Gaped } from 'components';
import { useBoolean } from 'hooks';
import { TEventListContext, TEventListState, EventDetails } from './types';
import { queryClient } from '../../utils/queryClient';
import { LOGS_QUERY_KEY } from 'hooks/query-keys';
import { SnackbarKey } from 'notistack';

const REFRESH_INTERVAL = 2500;

const EventListContext = createContext<TEventListContext>(null);

const GeneralSnackTypography = styled.span`
    color: white;
`;

export const EventListProvider = ({ children }: PropsWithChildren<{}>) => {
    const { t } = useTranslation();
    const { general, error, closeNotification } = useNotification();
    const [eventList, setEventList] = useState<TEventListState>({
        isNewNotification: false,
        events: {}
    });

    const [isOpen, show, hide] = useBoolean();

    const setNotificationRead = (index: number, workerId: string) => {
        setEventList((prevState) => {
            const newState = prevState;
            newState.events[workerId].isRead = true;

            return {
                ...newState,
                isNewNotification: !Object.values(newState.events)
                    .flat()
                    .every((item) => item.isRead)
            };
        });
    };

    const setAllNotificationsRead = () => {
        setEventList((prevState) => {
            Object.keys(prevState.events).forEach((key) => {
                prevState.events[key].isRead = true;
            });

            return {
                ...prevState,
                isNewNotification: false
            };
        });
    };

    const clearEvents = () => {
        setEventList({
            isNewNotification: false,
            events: {}
        });
    };

    useEffect(() => {
        if (Object.keys(eventList.events).length === 0 || isOpen) return;
        general(t('expressions.status_of_uploading_logs_updated'), {
            action: (key: SnackbarKey) => (
                <Gaped>
                    <Button
                        size="small"
                        onClick={() => {
                            setAllNotificationsRead();
                            show();
                            closeNotification(key);
                        }}>
                        <GeneralSnackTypography>
                            {t('expressions.show_message')}
                        </GeneralSnackTypography>
                    </Button>
                </Gaped>
            )
        });
        // eslint-disable-next-line
    }, [eventList.events, t, general, show]);

    const addToUploadWatchList = (
        workerId: string,
        projectName: string,
        projectId: number,
        logList: string[],
        projectUrl: string
    ) => {
        // Add initial queued state notification
        setEventList((prevState) => {
            return {
                isNewNotification: !isOpen,
                events: {
                    ...prevState.events,
                    [workerId]: {
                        logList,
                        projectName,
                        projectId,
                        jobSizeMegabytes: -1,
                        logsCount: -1,
                        progressPercent: 0,
                        status: 'uploadQueued',
                        isRead: false,
                        details: '',
                        time: new Date().toLocaleTimeString(),
                        projectUrl
                    }
                }
            };
        });

        // Create a loop to poll the backend for updates
        let intervalId = setInterval(async () => {
            try {
                // call API for updates
                const response: AxiosResponse<EventDetails> = await axios.get(
                    `/api/ui/upload/${workerId}`
                );

                // stop loop if in a finished state
                if (
                    ['Failed', 'Finished', 'Cancelled'].includes(
                        response.data.status
                    )
                ) {
                    clearInterval(intervalId);
                }

                // Handle too large request error
                if (response.data.status === 'Failed') {
                    if (
                        response.data.details.startsWith(
                            'Error: 413 - request entity too large'
                        )
                    ) {
                        error(
                            t(
                                'error.could_not_upload_timeline.too_large_request'
                            )
                        );
                    }
                }

                // add new notification or update previous
                setEventList((prevState) => {
                    const newState = JSON.parse(JSON.stringify(prevState));

                    // overwrite the notification with the new one
                    newState.events[workerId] = {
                        logList,
                        projectName,
                        projectId,
                        ...response.data,
                        status: `upload${response.data.status}`,
                        isRead: false,
                        progressPercent:
                            Math.floor(response.data?.progressPercent ??
                                -1),
                        time: new Date().toLocaleTimeString(),
                        projectUrl
                    };

                    return {
                        isNewNotification: !isOpen,
                        events: newState.events
                    };
                });
            } catch (error) {
                // if we get an error from the polling stop the interval and show failed notification
                clearInterval(intervalId);
                console.error(
                    `Failed to refresh upload worker status. Error: ${error}`
                );

                // add new failed notification
                setEventList((prevState) => {
                    const newState = JSON.parse(JSON.stringify(prevState));

                    // overwrite the notification with the new one
                    newState.events[workerId] = {
                        logList,
                        projectName,
                        projectId,
                        status: `uploadFailed`,
                        isRead: false,
                        progressPercent: '0.00',
                        time: new Date().toLocaleTimeString(),
                        projectUrl
                    };

                    return {
                        isNewNotification: !isOpen,
                        events: newState.events
                    };
                });
            }
        }, REFRESH_INTERVAL);
    };

    const addToTransformationWatchList = (
        workerId: string,
        logList: string[]
    ) => {
        // Add initial queued state notification
        setEventList((prevState) => {
            return {
                isNewNotification: !isOpen,
                events: {
                    ...prevState.events,
                    [workerId]: {
                        logList,
                        projectName: '',
                        projectId: 0,
                        jobSizeMegabytes: -1,
                        logsCount: -1,
                        progressPercent: 0,
                        status: 'transformationQueued',
                        isRead: false,
                        details: '',
                        time: new Date().toLocaleTimeString()
                    }
                }
            };
        });

        // Create a loop to poll the backend for updates
        const intervalId = setInterval(async () => {
            try {
                // call API for updates
                const response: AxiosResponse<EventDetails> = await axios.get(
                    `/api/ui/transformations/${workerId}`
                );
                // stop loop if in a finished state
                if (
                    response.data.status === 'Failed' ||
                    response.data.status === 'Finished' ||
                    response.data.status === 'Cancelled'
                ) {
                    clearInterval(intervalId);
                }

                // add new notification or update previous
                setEventList((prevState) => {
                    const newState = JSON.parse(JSON.stringify(prevState));

                    // overwrite the notification with the new one
                    newState.events[workerId] = {
                        logList,
                        projectName: '',
                        projectId: 0,
                        ...response.data,
                        status: `transformation${response.data.status}`,
                        isRead: false,
                        progressPercent:
                            Math.floor(response.data?.progressPercent ??
                                -1) as number,
                        time: new Date().toLocaleTimeString()
                    };

                    return {
                        isNewNotification: !isOpen,
                        events: newState.events
                    };
                });
            } catch (error) {
                console.error(
                    `Failed to refresh transformation worker status. Error: ${error}`
                );
            }
        }, REFRESH_INTERVAL);
    };

    const addToObfuscationWatchList = (workerId: string, log: string) => {
        setEventList((prevState) => {
            return {
                isNewNotification: !isOpen,
                events: {
                    ...prevState.events,
                    [workerId]: {
                        logList: [log],
                        projectName: '',
                        projectId: 0,
                        jobSizeMegabytes: -1,
                        logsCount: -1,
                        progressPercent: 0,
                        status: 'obfuscationQueued',
                        isRead: false,
                        details: '',
                        time: new Date().toLocaleTimeString()
                    }
                }
            };
        });

        // Create a loop to poll the backend for updates
        const intervalId = setInterval(async () => {
            try {
                // call API for updates
                const response: AxiosResponse<EventDetails> = await axios.get(
                    `/api/ui/timelineLogs/obfuscate/status/${workerId}`
                );

                // stop loop if in a finished state
                if (
                    response.data.status === 'Failed' ||
                    response.data.status === 'Finished' ||
                    response.data.status === 'Cancelled'
                ) {
                    clearInterval(intervalId);
                }

                // add new notification or update previous
                setEventList((prevState) => {
                    const newState = JSON.parse(JSON.stringify(prevState));

                    // overwrite the notification with the new one
                    newState.events[workerId] = {
                        logList: [log],
                        projectName: '',
                        projectId: 0,
                        ...response.data,
                        status: `obfuscation${response.data.status}`,
                        isRead: false,
                        progressPercent:
                            Math.floor(response.data?.progressPercent ??
                                -1) as number,
                        time: new Date().toLocaleTimeString()
                    };

                    return {
                        isNewNotification: !isOpen,
                        events: newState.events
                    };
                });
            } catch (error) {
                console.error(
                    `Failed to refresh obfuscation worker status. Error: ${error}`
                );
            }
        }, REFRESH_INTERVAL);
    };

    const addToDeletionWatchList = (workerId: string, logList: string[]) => {
        // Add initial queued state notification
        setEventList((prevState) => {
            return {
                isNewNotification: !isOpen,
                events: {
                    ...prevState.events,
                    [workerId]: {
                        logList,
                        projectName: '',
                        projectId: 0,
                        jobSizeMegabytes: -1,
                        logsCount: -1,
                        progressPercent: 0,
                        status: 'deletionQueued',
                        isRead: false,
                        details: '',
                        time: new Date().toLocaleTimeString()
                    }
                }
            };
        });

        // Create a loop to poll the backend for updates
        const intervalId = setInterval(async () => {
            try {
                // call API for updates
                const response: AxiosResponse<EventDetails> = await axios.get(
                    `/api/ui/timelinelogs/deletion/${workerId}`
                );
                // stop loop if in a finished state
                if (
                    response.data.status === 'Failed' ||
                    response.data.status === 'Finished' ||
                    response.data.status === 'Cancelled'
                ) {
                    clearInterval(intervalId);
                    await queryClient.invalidateQueries(LOGS_QUERY_KEY);
                }

                // add new notification or update previous
                setEventList((prevState) => {
                    const newState = JSON.parse(JSON.stringify(prevState));

                    // overwrite the notification with the new one
                    newState.events[workerId] = {
                        logList,
                        projectName: '',
                        projectId: 0,
                        ...response.data,
                        status: `deletion${response.data.status}`,
                        isRead: false,
                        progressPercent:
                            Math.floor(response.data?.progressPercent ??
                                -1) as number,
                        time: new Date().toLocaleTimeString()
                    };

                    return {
                        isNewNotification: !isOpen,
                        events: newState.events
                    };
                });
            } catch (error) {
                console.error(
                    `Failed to refresh transformation worker status. Error: ${error}`
                );
            }
        }, REFRESH_INTERVAL);
    };

    const cancelUpload = async (workerId: string) => {
        try {
            await axios.delete(`/api/ui/upload/${workerId}`);
        } catch (error) {
            general(t('error.upload_not_cancellable'));
            console.error(`Failed to cancel upload. Error: ${error}`);
        }
    };

    const cancelTransformation = async (workerId: string) => {
        try {
            await axios.delete(`/api/ui/transformations/${workerId}`);
        } catch (error) {
            general(t('error.transformation_not_cancellable'));
            console.error(`Failed to cancel transformation. Error: ${error}`);
        }
    };

    const cancelDeletion = async (workerId: string) => {
        try {
            await axios.delete(`/api/ui/timelinelogs/deletion/${workerId}`);
        } catch (error) {
            general(t('error.deletion_not_cancellable'));
            console.error(`Failed to cancel deletion. Error: ${error}`);
        }
    };

    return (
        <EventListContext.Provider
            value={{
                addToUploadWatchList,
                addToObfuscationWatchList,
                setNotificationRead,
                eventList,
                show,
                hide,
                isOpen,
                setAllNotificationsRead,
                clearEvents,
                cancelUpload,
                addToTransformationWatchList,
                cancelTransformation,
                addToDeletionWatchList,
                cancelDeletion
            }}>
            {children}
        </EventListContext.Provider>
    );
};

export const useEventList = () => useContext(EventListContext);
